import { IAppointment, IClinic, IProvider } from "interfaces";
import { Apis, Constants, Enums, Helpers, Store } from "utils";

export interface IAppointmentService {
  all(): Promise<any>;
  slice(healthGoalIds?: number[], offset?: number, count?: number): Promise<any>;
  journey(offset?: number, count?: number): Promise<any>;
  period(period: Enums.EnumPeriod): Promise<any>;
  week(offset?: number, count?: number): Promise<any>;
  today(state: boolean): Promise<any>;
  checkIn(id: number): Promise<any>;
  create(
    calendarId: string,
    eventId: string,
    serviceId: number,
    scheduledTime: Date,
    reason: string,
    healthGoals: number[],
    patientId: number | null,
    walkIn?: boolean,
    qiosk?: boolean
  ): Promise<any>;
  find(id: number): Promise<any>;
  updateSchedule(id: number, scheduledTime: string): Promise<any>;
  updateCheckIn(id: number, state: boolean, patientId?: number, qiosk?: boolean): Promise<any>;
  updateStartTime(id: number, state: boolean, patientId?: number): Promise<any>;
  updateEndTime(id: number, state: boolean, patientId?: number): Promise<any>;
  updateConsent(id: number, state: boolean): Promise<any>;
  updateProvider(id: number, providerId: number, patientId?: number): Promise<any>;
  updateService(id: number, serviceId: number, patientId?: number): Promise<any>;
  updateNotes(id: number, appointmentNotes: string, patientId?: number): Promise<any>;
  delete(id: number): Promise<any>;
  getDataByUrl(url: String): Promise<any>;
}

export class AppointmentService implements IAppointmentService {
  private qubehub: any;
  private controllerName: string = "appointment";

  constructor(accessToken: string) {
    this.qubehub = Apis.qubehub(accessToken);
  }

  async getDataByUrl(url: String): Promise<any> {
    return this.#handleResponse(await this.qubehub.get(url));
  }
  /**
   * fetch all the user's appointments
   * @returns list of appointments
   */

  async all() {
    return await this.slice([], 0, Constants.MAX_INTEGER);
  }

  /**
   * fetch a slice of the user's appointments
   * @param {number} offset starting point for pagination purposes e.g. 10 it will get from appointment 10 - 10 + count
   * @param {number} count quatity of appointments to fetch e.g. 10 it will get 10 appointments starting point + 10
   * @returns list of appointments
   */

  async slice(healthGoalIds: number[] = [], offset: number = 0, count: number = Constants.PAGE_LENGTH) {
    try {
      const [status, response] = this.#handleResponse(
        healthGoalIds.length > 0
          ? await this.qubehub.get(Helpers.toQueryString(this.controllerName, { healthGoalIds: healthGoalIds.join(","), offset, count }))
          : await this.qubehub.get(Helpers.toQueryString("appointment", { offset, count }))
      );
      return response ?? [];
    } catch (err: any) {
      console.log(err);
    }
    return [];
  }

  /**
   * fetch the appointments for the journey
   * @param {number} offset starting point for pagination purposes e.g. 10 it will get from appointment 10 - 10 + count
   * @param {number} count quatity of appointments to fetch e.g. 10 it will get 10 appointments starting point + 10
   * @returns list of appointments
   */

  async journey(offset: number = 0, count: number = Constants.PAGE_LENGTH) {
    try {
      const today = new Date();
      const [from, to] = Helpers.getDateRange(new Date(today.setDate(today.getDate() - 3)), count);
      const [status, response] = this.#handleResponse(await this.qubehub.get(Helpers.toQueryString(this.controllerName, { offset, count, from, to })));
      return response ?? [];
    } catch (err: any) {
      console.log(err);
    }
    return [];
  }

  async period(period: Enums.EnumPeriod) {
    try {
      const today = new Date();
      const [from, to] = Helpers.getDateRange(new Date(today.setDate(today.getDate() - period)), period);
      const [status, response] = this.#handleResponse(await this.qubehub.get(Helpers.toQueryString(this.controllerName, { from, to })));
      return response ?? [];
    } catch (err: any) {
      console.log(err);
    }
    return [];
  }

  /**
   * fetch the appointments for the next 7 days
   * @param {number} offset starting point for pagination purposes e.g. 10 it will get from appointment 10 - 10 + count
   * @param {number} count quatity of appointments to fetch e.g. 10 it will get 10 appointments starting point + 10
   * @returns list of appointments
   */

  async week(offset: number = 0, count: number = Constants.PAGE_LENGTH) {
    try {
      const [from, to] = Helpers.getDateRange();
      const [status, response] = this.#handleResponse(await this.qubehub.get(Helpers.toQueryString(this.controllerName, { offset, count, from, to })));
      return response ?? [];
    } catch (err: any) {
      console.log(err);
    }
    return [];
  }

  /**
   * fetch the appointments for today
   * @param {boolean} state true for booked and false for waiting
   * @returns list of appointments
   */

  async today(state: boolean) {
    try {
      return this.#handleResponse(await this.qubehub.get(Helpers.toQueryString(this.controllerName + "/stats/d", { state })));
    } catch (err: any) {
      console.log(err);
    }
    return [];
  }

  /**
   * fetch the patients who have checked in
   * @param {number} id appointment id
   * @returns list of appointments
   */

  async checkIn(id: number) {
    try {
      return this.#handleResponse(await this.qubehub.get(Helpers.interpolateURL(this.controllerName + "/checkin/:appointmentId", { appointmentId: id })));
    } catch (err: any) {
      console.log(err);
    }
    return [];
  }

  /**
   * create an appointment
   * @param {string} calendarId google calendar id
   * @param {string} eventId google calendar event id
   * @param {number} serviceId service where the appointment reffer to
   * @param {number} scheduledStartTime starting time of the appointment
   * @param {number} scheduledEndTime ending time of the appointment
   * @returns
   */

  async create(
    calendarId: string,
    eventId: string,
    serviceId: number,
    scheduledTime: Date,
    reason: string,
    healthGoalIds: number[],
    patientId: number,
    walkIn: boolean = false,
    qiosk: boolean = false
  ) {
    try {
      const props = { calendarId, eventId, serviceId, scheduledTime: Helpers.toOffsetString(scheduledTime), reason, patientId, walkIn, qiosk };
      return this.#handleResponse(await this.qubehub.post(this.controllerName, healthGoalIds.length > 0 ? { ...props, healthGoalIds: healthGoalIds.join(",") } : props));
    } catch (err: any) {
      console.log(err);
    }
    return null;
  }

  /**
   * fetch an appointment
   * @param {number} id appointment id
   * @returns
   */

  async find(id: number) {
    try {
      const [status, response] = this.#handleResponse(await this.qubehub.get(Helpers.interpolateURL(this.controllerName + "/:appointmentId", { appointmentId: id })));
      return response ? { appointmentId: id, ...response } : {};
    } catch (err: any) {
      console.log(err);
    }
    return null;
  }

  /**
   * update the scheduled time of an appointment
   * @param {number} id appointment id
   * @param {string} scheduledTime time of the appointment
   * @returns
   */

  async updateSchedule(id: number, scheduledTime: string) {
    try {
      this.#handleResponse(await this.qubehub.patch(Helpers.interpolateURL(this.controllerName + "/:appointmentId", { appointmentId: id }), { scheduledTime }));
    } catch (err: any) {
      console.log(err);
    }
    return null;
  }

  /**
   * update the check-in status of an appointment
   * @param {number} id appointment id
   * @param {boolean} state whether the endTime should be added or removed (true for setting and false for removing)
   * @returns
   */

  async updateCheckIn(id: number, state: boolean, patientId?: number, qiosk: boolean = false) {
    try {
      return this.#handleResponse(
        await this.qubehub.patch(Helpers.interpolateURL(this.controllerName + "/:appointmentId", { appointmentId: id }), {
          patientId,
          checkIn: state,
          ...(state ? {} : { start: false, end: false }),
          qiosk,
        })
      );
    } catch (err: any) {
      console.log(err);
    }
    return null;
  }

  /**
   * update the start time status of an appointment
   * @param {number} id appointment id
   * @param {boolean} state whether the endTime should be added or removed (true for setting and false for removing)
   * @returns
   */

  async updateStartTime(id: number, state: boolean, patientId: number) {
    try {
      this.#handleResponse(
        await this.qubehub.patch(Helpers.interpolateURL(this.controllerName + "/:appointmentId", { appointmentId: id }), {
          patientId,
          start: state,
          ...(state ? {} : { end: false }),
        })
      );
    } catch (err: any) {
      console.log(err);
    }
    return null;
  }

  /**
   * update the end time status of an appointment
   * @param {number} id appointment id
   * @param {boolean} state whether the endTime should be added or removed (true for setting and false for removing)
   * @returns
   */

  async updateEndTime(id: number, state: boolean, patientId: number) {
    try {
      this.#handleResponse(await this.qubehub.patch(Helpers.interpolateURL(this.controllerName + "/:appointmentId", { appointmentId: id }), { patientId, end: state }));
    } catch (err: any) {
      console.log(err);
    }
    return null;
  }

  /**
   * update the consent status of an appointment
   * @param {number} id appointment id
   * @param {boolean} state whether the consented for was completed
   * @returns
   */

  async updateConsent(id: number, state: boolean) {
    try {
      this.#handleResponse(await this.qubehub.patch(Helpers.interpolateURL(this.controllerName + "/:appointmentId", { appointmentId: id }), { consented: state }));
    } catch (err: any) {
      console.log(err);
    }
    return null;
  }

  async updateProvider(id: number, providerId: number, patientId?: number) {
    try {
      this.#handleResponse(await this.qubehub.patch(Helpers.interpolateURL(this.controllerName + "/:appointmentId", { appointmentId: id }), { providerId, patientId }));
    } catch (err: any) {
      console.log(err);
    }
    return null;
  }

  async updateService(id: number, serviceId: number, patientId?: number) {
    try {
      this.#handleResponse(await this.qubehub.patch(Helpers.interpolateURL(this.controllerName + "/:appointmentId", { appointmentId: id }), { serviceId, patientId }));
    } catch (err: any) {
      console.log(err);
    }
    return null;
  }

  async updateNotes(id: number, appointmentNotes: string, patientId?: number) {
    try {
      return this.#handleResponse(
        await this.qubehub.patch(Helpers.interpolateURL(this.controllerName + "/:appointmentId", { appointmentId: id }), { appointmentNotes, patientId })
      );
    } catch (err: any) {
      console.log(err);
    }
    return null;
  }

  /**
   * delete an appointment
   * @param {number} id appointment id
   * @returns
   */

  async delete(id: number) {
    try {
      this.#handleResponse(await this.qubehub.delete(Helpers.interpolateURL(this.controllerName + "/:appointmentId", { appointmentId: id })));
    } catch (err: any) {
      console.log(err);
    }
  }

  /**
   * method to handle the response from the API
   * @param {any} response the data retrieved from the API
   * @returns
   */

  #handleResponse(response: any) {
    if (response && response.data) {
      const { status, payload } = response.data;
      if (Enums.EnumResponse.Unauthorized === status) Store.sessionStorage.clear();
      return payload ? [status, payload] : [status, null];
    }
    return [Enums.EnumResponse.None, null];
  }
}
